import { FileResults, searchOptionsType } from 'features/files/files-summary/types';
import { OrderInsightsEmailPreference } from 'ui-kit/components/modal/OrderInsightsEmailPreferenceDrawer';
import { handleError } from 'utilities/utilities';
import { getAgencyApiHost, getApiHost } from '../host-by-env';
import { ICreateFileResponse } from './interfaces/create-file';
import { fileInfoType, propertyType } from './interfaces/get-file';
import {
  FileOrderInsightsType,
  ICancelRequestPayload,
  ICancelRequestResponse,
  ISavePhoneNumberPayload,
} from './interfaces/get-file-order-insights';
import { IVerifyAddressResponse } from './interfaces/verify-address';

/* Use Async to Handle Property Details */

export type GetFileInfoProps = {
  fileId?: string;
};

/* Use Async GET Request */
export async function getFileInfo(fileId: string, token: string): Promise<fileInfoType> {
  const url = `${getApiHost()}file/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

/* Use Async POST Request */
export async function createFile(payload: ICreateFileResponse, token: string): Promise<string> {
  const url = `${getApiHost()}file/`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });

  const json = await response.json();
  const { result } = json;
  handleError(json);
  return result?.fileId;
}

/* Use Async PUT Request */
export const saveFile = async (
  payload: ICreateFileResponse | undefined | null,
  fileId: string,
  token: string,
): Promise<ICreateFileResponse> => {
  const url = `${getApiHost()}file/${fileId}`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
};

export async function searchFiles(
  payload: searchOptionsType | string,
  token: string,
): Promise<{ result: FileResults; errorCode?: string | number }> {
  const url = ` ${getApiHost()}search/search-files`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  return await response.json();
}

export const saveFileStatus = async (payload: any, token: string): Promise<any> => {
  const url = `${getApiHost()}file/status`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
};

export async function getFileDetail(fileId: string | undefined, token: string): Promise<unknown> {
  const url = `${getApiHost()}file/summary/${fileId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getFileOrderInsights(fileId: string, token: string): Promise<FileOrderInsightsType> {
  const url = `${getApiHost()}file/${fileId}/order-insights`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export const savePhoneNumber = async (
  payload: ISavePhoneNumberPayload,
  token: string,
): Promise<ISavePhoneNumberPayload> => {
  const url = `${getApiHost()}marketplace/order-insights/party`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
};

export async function refreshFileOrderInsights(fileId: string, token: string): Promise<boolean> {
  const url = `${getApiHost()}file/${fileId}/order-insights/refresh`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getOrderInsightsEmailPreference(
  firmId: string,
  token: string,
): Promise<OrderInsightsEmailPreference> {
  const url = `${getApiHost()}file/order-insights/${firmId}/email-subscription`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export const UpdateOrderInsightsEmailPreference = async (payload: any, token: string): Promise<any> => {
  const url = `${getApiHost()}file/order-insights/update-email-subscription`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
};

export async function getFileSummaryReport(fileId: string, token: string, firmId?: string): Promise<unknown> {
  const url = `${getApiHost()}file/summaryReport/${fileId}?pdfToken=${token}${firmId ? `&firmId=${firmId}` : ''}`;
  const response = await fetch(url, {
    method: 'GET',
    //headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export async function getIntelliCheckReport(fileId: string, token: string, orderId?: string): Promise<unknown> {
  const url = `${getApiHost()}file/${fileId}/identity-verification-data?orderId=${orderId}&pdfToken=${token}`;
  const response = await fetch(url, {
    method: 'GET',
  });
  const json = await response.json();
  handleError(json);
  return json.result;
}

export const saveFileReopen = async (payload: any, token: string): Promise<any> => {
  const url = `${getApiHost()}remittance/file/reopen`;
  let fileReopenResponse: any = {};
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });

  const json = await response.json();
  if (json.messages && json.messages.length > 0) {
    fileReopenResponse = { messages: json.messages, result: false };
  } else {
    fileReopenResponse = { result: json.result };
  }
  return fileReopenResponse;
};

export async function clearUserCache(login: string, token: string): Promise<boolean> {
  const url = `${getApiHost()}file/deleteCache/User-Profile/${login}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return true;
}

export async function verifyAddress(property: propertyType, token: string): Promise<IVerifyAddressResponse[]> {
  const url = `${getApiHost()}file/verifyAddress`;
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({
      address1: property.address1,
      address2: property.address2,
      city: property.city,
      state: property.state,
      postalCode: property.postalCode,
      county: property.county,
      apn: property.apn,
      verifyAddressType: property.propertyIdentificationMethod,
    }),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  const { result } = json;
  handleError(json);
  return result.map((item: any) => ({
    address1: item.Address1,
    address2: item.Address2,
    city: item.City,
    zip: item.Zip,
    county: item.County,
    apn: item.Apn,
    propertyDetailId: item.PropertyDetailId,
    stateCode: item.StateCode,
  }));
}

export async function getIdvDetailedJson(fileId: string, orderId: string, token: string): Promise<unknown> {
  const url = `${getAgencyApiHost()}file-service/v1/files/${fileId}/identity-verification-data?orderId=${orderId}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result ?? json;
}

export const cancelIdentityVerificationRequest = async (
  payload: ICancelRequestPayload,
  token: string,
): Promise<ICancelRequestResponse> => {
  const url = `${getApiHost()}marketplace/services/identity-verification/user-cancel`;
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(payload),
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  const json = await response.json();
  handleError(json);
  return json.result;
};
